import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Typography, 
    Progress, 
    Badge, 
    Avatar, 
    List, 
    Tag, 
    Button, 
    Modal, 
    Space, 
    Tooltip,
    Divider,
    Statistic
} from 'antd';
import { 
    TrophyOutlined, 
    StarOutlined, 
    FireOutlined, 
    CrownOutlined,
    GiftOutlined,
    ThunderboltOutlined,
    HeartOutlined,
    ShoppingCartOutlined,
    MessageOutlined,
    CalendarOutlined,
    DollarOutlined,
    RocketOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const { Title, Text } = Typography;

const GamificationSystem = ({ 
    showLevel = true,
    showBadges = true,
    showAchievements = true,
    showLeaderboard = false,
    compact = false
}) => {
    const { user } = useAuth();
    const [gamification, setGamification] = useState({});
    const [loading, setLoading] = useState(true);
    const [achievementModalOpen, setAchievementModalOpen] = useState(false);
    const [selectedAchievement, setSelectedAchievement] = useState(null);

    useEffect(() => {
        fetchGamificationData();
    }, [user]);

    const fetchGamificationData = async () => {
        try {
            setLoading(true);
            const response = await api.getGamificationData();
            if (response.success) {
                setGamification(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch gamification data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getLevelInfo = (points) => {
        const levels = [
            { level: 1, name: 'Newcomer', min: 0, max: 100, color: '#8c8c8c', icon: '🌱' },
            { level: 2, name: 'Explorer', min: 100, max: 300, color: '#52c41a', icon: '🚀' },
            { level: 3, name: 'Foodie', min: 300, max: 600, color: '#1890ff', icon: '🍽️' },
            { level: 4, name: 'Connoisseur', min: 600, max: 1000, color: '#722ed1', icon: '👑' },
            { level: 5, name: 'Master', min: 1000, max: 1500, color: '#fa8c16', icon: '🏆' },
            { level: 6, name: 'Legend', min: 1500, max: 9999, color: '#f5222d', icon: '⭐' }
        ];

        const currentLevel = levels.find(l => points >= l.min && points < l.max) || levels[levels.length - 1];
        const nextLevel = levels.find(l => l.level === currentLevel.level + 1);
        const progress = nextLevel ? 
            ((points - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100 : 100;

        return { currentLevel, nextLevel, progress };
    };

    const getBadgeIcon = (badgeType) => {
        const icons = {
            'first_order': <ShoppingCartOutlined />,
            'reviewer': <StarOutlined />,
            'loyal_customer': <HeartOutlined />,
            'social_butterfly': <MessageOutlined />,
            'early_bird': <CalendarOutlined />,
            'big_spender': <DollarOutlined />,
            'explorer': <RocketOutlined />,
            'perfectionist': <CheckCircleOutlined />
        };
        return icons[badgeType] || <TrophyOutlined />;
    };

    const getBadgeColor = (badgeType) => {
        const colors = {
            'first_order': 'blue',
            'reviewer': 'gold',
            'loyal_customer': 'red',
            'social_butterfly': 'purple',
            'early_bird': 'green',
            'big_spender': 'orange',
            'explorer': 'cyan',
            'perfectionist': 'magenta'
        };
        return colors[badgeType] || 'default';
    };

    const renderLevelCard = () => {
        if (!showLevel) return null;

        const { currentLevel, nextLevel, progress } = getLevelInfo(gamification.points || 0);

        return (
            <Card 
                title={
                    <div className="flex items-center gap-2">
                        <CrownOutlined />
                        Level {currentLevel.level} - {currentLevel.name}
                    </div>
                }
                size="small"
                className="level-card"
            >
                <div className="space-y-4">
                    <div className="text-center">
                        <div className="text-4xl mb-2">{currentLevel.icon}</div>
                        <Title level={3} style={{ color: currentLevel.color, margin: 0 }}>
                            {currentLevel.name}
                        </Title>
                        <Text type="secondary">
                            {gamification.points || 0} points
                        </Text>
                    </div>

                    {nextLevel && (
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <Text strong>Progress to Level {nextLevel.level}</Text>
                                <Text type="secondary">
                                    {Math.round(progress)}%
                                </Text>
                            </div>
                            <Progress 
                                percent={Math.round(progress)} 
                                strokeColor={nextLevel.color}
                                showInfo={false}
                            />
                            <div className="flex justify-between items-center mt-1">
                                <Text className="text-xs">
                                    {nextLevel.min - (gamification.points || 0)} points to go
                                </Text>
                                <Text className="text-xs" style={{ color: nextLevel.color }}>
                                    {nextLevel.name}
                                </Text>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const renderBadges = () => {
        if (!showBadges) return null;

        const badges = gamification.badges || [];
        const earnedBadges = badges.filter(b => b.earned);
        const availableBadges = badges.filter(b => !b.earned);

        return (
            <Card title="Badges" size="small">
                <div className="space-y-4">
                    <div>
                        <Title level={5}>Earned Badges ({earnedBadges.length})</Title>
                        <Row gutter={[8, 8]}>
                            {earnedBadges.map(badge => (
                                <Col key={badge.id}>
                                    <Tooltip title={badge.description}>
                                        <Badge 
                                            count={badge.earned ? <CheckCircleOutlined /> : 0}
                                            offset={[-5, 5]}
                                        >
                                            <Avatar 
                                                size={48}
                                                icon={getBadgeIcon(badge.type)}
                                                style={{ 
                                                    backgroundColor: getBadgeColor(badge.type) === 'default' ? '#1890ff' : undefined,
                                                    color: 'white'
                                                }}
                                            />
                                        </Badge>
                                    </Tooltip>
                                </Col>
                            ))}
                        </Row>
                    </div>

                    {availableBadges.length > 0 && (
                        <div>
                            <Title level={5}>Available Badges</Title>
                            <Row gutter={[8, 8]}>
                                {availableBadges.slice(0, 6).map(badge => (
                                    <Col key={badge.id}>
                                        <Tooltip title={badge.description}>
                                            <Avatar 
                                                size={48}
                                                icon={getBadgeIcon(badge.type)}
                                                style={{ 
                                                    backgroundColor: '#f5f5f5',
                                                    color: '#bfbfbf'
                                                }}
                                            />
                                        </Tooltip>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const renderAchievements = () => {
        if (!showAchievements) return null;

        const achievements = gamification.achievements || [];

        return (
            <Card 
                title="Achievements" 
                size="small"
                extra={
                    <Button 
                        type="link" 
                        onClick={() => setAchievementModalOpen(true)}
                    >
                        View All
                    </Button>
                }
            >
                <List
                    dataSource={achievements.slice(0, 3)}
                    renderItem={(achievement) => (
                        <List.Item>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-3">
                                    <Avatar 
                                        icon={<TrophyOutlined />}
                                        style={{ 
                                            backgroundColor: achievement.completed ? '#faad14' : '#f5f5f5',
                                            color: achievement.completed ? 'white' : '#bfbfbf'
                                        }}
                                    />
                                    <div>
                                        <Text strong={achievement.completed}>
                                            {achievement.title}
                                        </Text>
                                        <div className="text-xs text-gray-500">
                                            {achievement.description}
                                        </div>
                                    </div>
                                </div>
                                {achievement.completed ? (
                                    <Tag color="green">Completed</Tag>
                                ) : (
                                    <Progress 
                                        percent={achievement.progress} 
                                        size="small" 
                                        style={{ width: 60 }}
                                    />
                                )}
                            </div>
                        </List.Item>
                    )}
                />
            </Card>
        );
    };

    const renderLeaderboard = () => {
        if (!showLeaderboard) return null;

        const leaderboard = gamification.leaderboard || [];

        return (
            <Card title="Leaderboard" size="small">
                <List
                    dataSource={leaderboard}
                    renderItem={(user, index) => (
                        <List.Item>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-3">
                                    <div className="w-6 text-center">
                                        {index < 3 ? (
                                            <span className="text-lg">
                                                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                            </span>
                                        ) : (
                                            <Text strong>#{index + 1}</Text>
                                        )}
                                    </div>
                                    <Avatar src={user.avatar} />
                                    <div>
                                        <Text strong>{user.name}</Text>
                                        <div className="text-xs text-gray-500">
                                            Level {user.level} • {user.points} points
                                        </div>
                                    </div>
                                </div>
                                <Tag color="blue">{user.points}</Tag>
                            </div>
                        </List.Item>
                    )}
                />
            </Card>
        );
    };

    const renderStats = () => {
        const stats = gamification.stats || {};

        return (
            <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                    <Statistic
                        title="Total Points"
                        value={gamification.points || 0}
                        prefix={<StarOutlined />}
                        valueStyle={{ color: '#faad14' }}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic
                        title="Badges Earned"
                        value={gamification.badges?.filter(b => b.earned).length || 0}
                        prefix={<TrophyOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic
                        title="Achievements"
                        value={gamification.achievements?.filter(a => a.completed).length || 0}
                        prefix={<CrownOutlined />}
                        valueStyle={{ color: '#722ed1' }}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <Statistic
                        title="Streak"
                        value={stats.current_streak || 0}
                        prefix={<FireOutlined />}
                        valueStyle={{ color: '#f5222d' }}
                        suffix="days"
                    />
                </Col>
            </Row>
        );
    };

    if (compact) {
        return (
            <Card size="small" className="gamification-compact">
                {renderStats()}
            </Card>
        );
    }

    return (
        <div className="gamification-system">
            {renderStats()}
            
            <div className="mt-4">
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        {renderLevelCard()}
                    </Col>
                    <Col xs={24} md={8}>
                        {renderBadges()}
                    </Col>
                    <Col xs={24} md={8}>
                        {renderAchievements()}
                    </Col>
                </Row>
            </div>

            {showLeaderboard && (
                <div className="mt-4">
                    {renderLeaderboard()}
                </div>
            )}

            <Modal
                title="All Achievements"
                open={achievementModalOpen}
                onCancel={() => setAchievementModalOpen(false)}
                footer={null}
                width={600}
            >
                <List
                    dataSource={gamification.achievements || []}
                    renderItem={(achievement) => (
                        <List.Item>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-3">
                                    <Avatar 
                                        icon={<TrophyOutlined />}
                                        style={{ 
                                            backgroundColor: achievement.completed ? '#faad14' : '#f5f5f5',
                                            color: achievement.completed ? 'white' : '#bfbfbf'
                                        }}
                                    />
                                    <div>
                                        <Text strong={achievement.completed}>
                                            {achievement.title}
                                        </Text>
                                        <div className="text-sm text-gray-600">
                                            {achievement.description}
                                        </div>
                                        {achievement.reward && (
                                            <div className="text-xs text-green-600">
                                                Reward: {achievement.reward}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {achievement.completed ? (
                                    <Tag color="green">Completed</Tag>
                                ) : (
                                    <div className="text-right">
                                        <Progress 
                                            percent={achievement.progress} 
                                            size="small" 
                                            style={{ width: 80 }}
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {achievement.progress}% complete
                                        </div>
                                    </div>
                                )}
                            </div>
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default GamificationSystem;
